import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
  fetchAllUsers,
  fetchUserSubscriptions,
  selectUsers,
  selectUserSubscriptions,
  selectLoading,
  updateUserStatus,
  removeUser
} from '../../../../app/store/slices/adminSlice';
import { IconButton, Tooltip, Chip} from '@mui/material';
import { FiEdit2, FiTrash2 } from 'react-icons/fi';
import StatusEditDialog from './components/StatusEditDialog';
import ConfirmationModal from '../../../shared/components/modals/ConfirmationModal';
import { toast } from 'react-toastify';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {PAGE_SIZE_OPTIONS, SORT_OPTIONS, ROLE_OPTIONS, STATUS_OPTIONS } from '../../../../utils/constants/userManagementConstants'

const UserManagement = () => {
  const dispatch = useDispatch();
  const { users, pagination } = useSelector(selectUsers) || { users: [], pagination: {} };
  const { subscriptions } = useSelector(selectUserSubscriptions) || { subscriptions: [] };
  const loading = useSelector(selectLoading);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusData, setStatusData] = useState({
    status: '',
    reason: ''
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('');
  const [billingCycle, setBilling] = useState('');
  const [role, setRole] = useState('');
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [pageSize, setPageSize] = useState(10)
  
  useEffect(() => {
    dispatch(fetchAllUsers({
      page: currentPage,
      limit:pageSize,
      searchTerm,
      status,
      role,
      sortBy,
      sortOrder,
      billingCycle,
    }));
    dispatch(fetchUserSubscriptions());
  }, [dispatch, currentPage, pageSize, searchTerm, status, billingCycle,  role, sortBy, sortOrder]);


  const handleClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleEdit = () => {
    handleClose();
  };

  const handleDelete = (userId, userName) => {
    setUserToDelete({ id: userId, name: userName });
    setShowDeleteModal(true);
  };
  
  const handleDeleteConfirm = async () => {
    if (!userToDelete) return;
    
    setIsDeleting(true);
    try {
      await dispatch(removeUser(userToDelete.id)).unwrap();
      toast.success(`User "${userToDelete.name}" has been successfully deleted.`);
      setShowDeleteModal(false);
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error(`Failed to delete user: ${error?.message || 'Unknown error occurred'}`);
    } finally {
      setIsDeleting(false);
      setUserToDelete(null);
    }
  };
  
  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setUserToDelete(null);
  };

  const handleStatusUpdate = (user) => {
    setSelectedUser(user);
    setStatusData({
      status: user.status,
      reason: user.statusReason || ''
    });
    setStatusDialogOpen(true);
  };

  const handleStatusSubmit = async () => {
    if (selectedUser && statusData.status) {
      dispatch(updateUserStatus({
        id: selectedUser._id,
        status: statusData.status,
        reason: statusData.reason
      }));
      setStatusDialogOpen(false);
      setSelectedUser(null);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const exportToCSV = () => {
    const headers = [
      'Name',
      'Email',
      'Plan',
      'Status',
      'Sub. Date',
      'Amount',
      'Billing',
      'Renewal'
    ];

    const csvContent = [
      headers.join(','),
      ...sortedUsers.map(user => {
        const subscription = subscriptions?.find(sub => sub.owner === user._id) || {};
        return [
          `"${user.name || ''}"`,
          `"${user.email || ''}"`,
          `"${subscription?.plan || '-'}"`,
          `"${user.status || ''}"`,
          `"${subscription.subscriptionDate ? formatDate(subscription.subscriptionDate) : '-'}"`,
          `"${subscription.amount || '-'}"`,
          `"${subscription.billingCycle || '-'}"`,
          `"${subscription.renewalDate ? formatDate(subscription.renewalDate) : '-'}"`
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    
    if (link.download !== undefined) {
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `users-report-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const exportToPDF = () => {
    const doc = new jsPDF();
    
    // Add title
    doc.setFontSize(18);
    doc.text('Users Management Report', 14, 20);
    doc.setFontSize(12);
    doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 14, 30);

    // Generate the table with column separators
    autoTable(doc, {
      columns: [
        { header: 'Name', dataKey: 'name' },
        { header: 'Email', dataKey: 'email' },
        { header: 'Plan', dataKey: 'plan' },
        { header: 'Status', dataKey: 'status' },
        { header: 'Sub. Date', dataKey: 'subDate' },
        { header: 'Amount', dataKey: 'amount' },
        { header: 'Billing', dataKey: 'billing' },
        { header: 'Renewal', dataKey: 'renewal' }
      ],
      body: sortedUsers.map(user => {
        const subscription = subscriptions?.find(sub => sub.owner === user._id) || {};
        return {
          name: user.name || '',
          email: user.email || '',
          plan: subscription?.plan || '-',
          status: user.status || '',
          subDate: subscription.subscriptionDate ? formatDate(subscription.subscriptionDate) : '-',
          amount: subscription.amount || '-',
          billing: subscription.billingCycle || '-',
          renewal: subscription.renewalDate ? formatDate(subscription.renewalDate) : '-'
        };
      }),
      startY: 40,
      styles: { 
        fontSize: 8,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: { 
        fillColor: [41, 128, 185],
        textColor: 255,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      bodyStyles: {
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      alternateRowStyles: { 
        fillColor: [245, 245, 245],
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      tableLineColor: [0, 0, 0],
      tableLineWidth: 0.1,
      margin: { top: 40 },
      willDrawCell: function(data) {
        if (data.column.index < data.table.columns.length - 1) {
          doc.setDrawColor(0, 0, 0);
          doc.setLineWidth(0.1);
          doc.line(
            data.cell.x + data.cell.width, 
            data.cell.y, 
            data.cell.x + data.cell.width, 
            data.cell.y + data.cell.height
          );
        }
      }
    });

    // Save the PDF
    doc.save('users-report.pdf');
  };

  const itemRange = useMemo(() => {
    if (!pagination?.total) return '';
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, pagination.total);
    return `Showing ${start} - ${end}`;
  }, [pagination?.total, currentPage, pageSize]);

  const sortedUsers = useMemo(() => {
    if (!users?.length) return [];
    
    let sorted = [...users];
    
    // Check if sorting by subscription fields
    const subscriptionFields = ['subscriptionDate', 'renewalDate', 'billingCycle', 'amount'];
    
    if (subscriptionFields.includes(sortBy)) {
      sorted.sort((a, b) => {
        const subA = subscriptions?.find(sub => sub.owner === a._id);
        const subB = subscriptions?.find(sub => sub.owner === b._id);
        
        let valueA, valueB;
        
        switch (sortBy) {
          case 'subscriptionDate':
          case 'renewalDate':
            valueA = subA?.[sortBy] ? new Date(subA[sortBy]).getTime() : 0;
            valueB = subB?.[sortBy] ? new Date(subB[sortBy]).getTime() : 0;
            break;
          case 'amount':
            valueA = parseFloat(subA?.amount) || 0;
            valueB = parseFloat(subB?.amount) || 0;
            break;
          default:
            valueA = subA?.[sortBy] || '';
            valueB = subB?.[sortBy] || '';
        }
        
        if (sortOrder === 'asc') {
          return valueA - valueB;
        } else {
          return valueB - valueA;
        }
      });
      
      return sorted;
    }
    
    return users;
  }, [users, subscriptions, sortBy, sortOrder]);

  return (
    <div className="p-4">
      <div className="bg-white rounded-lg shadow">
        {/* Header with Filters, Search, and Export */}
        <div className="p-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 border-b">
          <h2 className="text-xl font-semibold">User Management</h2>
          <div className="flex flex-wrap gap-2 items-center">
            <input
              type="text"
              placeholder="Search name/email"
              value={searchTerm}
              onChange={e => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              className="px-2 py-1 border rounded-md text-sm"
            />
            <select
              value={status}
              onChange={e => { setStatus(e.target.value); setCurrentPage(1); }}
              className="px-2 py-1 pr-7 border rounded-md text-sm"
            >
              {STATUS_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            {/* <select
              value={billingCycle}
              onChange={e => { setBilling(e.target.value); setCurrentPage(1); }}
              className="px-2 py-1 pr-7 border rounded-md text-sm"
            >
              {BILLING_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select> */}
            <select
              value={role}
              onChange={e => { setRole(e.target.value); setCurrentPage(1); }}
              className="px-2 py-1 pr-7 border rounded-md text-sm"
            >
              {ROLE_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <select
              value={sortBy}
              onChange={e => { setSortBy(e.target.value); setCurrentPage(1); }}
              className="px-2 py-1 pr-7 border rounded-md text-sm"
            >
              {SORT_OPTIONS.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
            </select>
            <button
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="px-2 py-1 border rounded-md text-sm"
              title="Toggle sort order"
            >
              {sortOrder === 'asc' ? 'Asc' : 'Desc'}
            </button>

            {/* Export dropdown */}
            <div className="relative">
              <select
                onChange={e => {
                  const format = e.target.value;
                  if (format === 'pdf') {
                    exportToPDF();
                  } else if (format === 'csv') {
                    exportToCSV();
                  }
                  // Reset to default after export
                  e.target.value = '';
                }}
                className="px-4 py-2 bg-btn-primary text-white rounded-md hover:bg-btn-primary/90 text-sm cursor-pointer"
                disabled={loading || users?.length === 0}
                defaultValue=""
              >
                <option value="" disabled>Export</option>
                <option value="pdf">Export PDF</option>
                <option value="csv">Export CSV</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table Container */}
        <div className="overflow-x-auto overflow-y-auto max-h-[60vh] relative">
          <div className="min-w-full inline-block align-middle">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50 sticky top-0 z-20 shadow-sm border-b border-gray-200">
                <tr>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] bg-gray-50">
                    Name
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[200px] bg-gray-50">
                    Email
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] bg-gray-50">
                    Plan
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] bg-gray-50">
                    Sub. Date
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] bg-gray-50">
                    Amount
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] bg-gray-50">
                    Billing Cycle
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[100px] bg-gray-50">
                    Renewal
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[80px] bg-gray-50">
                    Status
                  </th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider min-w-[120px] bg-gray-50">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center">
                      <div className="flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-btn-primary"></div>
                      </div>
                    </td>
                  </tr>
                ) : users?.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                      No users found
                    </td>
                  </tr>
                ) : (
                  sortedUsers?.map((user) => {
                    const subscription = subscriptions?.find(sub => sub.owner === user._id) || {};
                    return (
                      <tr key={user._id} className="hover:bg-gray-50">
                        <td className="px-3 sm:px-6 py-4 text-sm font-medium text-gray-900 max-w-[150px]">
                          <div className="truncate" title={user.name}>
                            {user.name}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 max-w-[250px]">
                          <div className="break-all" title={user.email}>
                            {user.email}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 max-w-[120px]">
                          <div className="truncate" title={subscription?.plan || '-'}
                          >
                            {subscription?.plan || '-'}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {subscription.subscriptionDate ? formatDate(subscription.subscriptionDate) : '-'}
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {subscription.amount || '-'}
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 capitalize max-w-[100px]">
                          <div className="truncate" title={subscription.billingCycle || '-'}
                          >
                            {subscription.billingCycle || '-'}
                          </div>
                        </td>
                        <td className="px-3 sm:px-6 py-4 text-sm text-gray-500 whitespace-nowrap">
                          {subscription.renewalDate ? formatDate(subscription.renewalDate) : '-'}
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.status?.toLowerCase() === 'active' 
                              ? 'bg-green-100 text-green-800' 
                              : user.status?.toLowerCase() === 'suspended'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-3 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center justify-center space-x-2">
                            <Tooltip title="Edit Status" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleStatusUpdate(user)}
                                className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                                sx={{ 
                                  '&:hover': { 
                                    backgroundColor: 'rgba(59, 130, 246, 0.1)' 
                                  } 
                                }}
                              >
                                <FiEdit2 size={16} />
                              </IconButton>
                            </Tooltip>
                            
                            {/* Delete User Button */}
                            <Tooltip title="Delete User" arrow>
                              <IconButton
                                size="small"
                                onClick={() => handleDelete(user._id, user.name)}
                                className="text-red-600 hover:text-red-800 hover:bg-red-50"
                                sx={{ 
                                  '&:hover': { 
                                    backgroundColor: 'rgba(239, 68, 68, 0.1)' 
                                  } 
                                }}
                              >
                                <FiTrash2 size={16} />
                              </IconButton>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Status Update Dialog */}
        <StatusEditDialog 
          open={statusDialogOpen} 
          onClose={() => setStatusDialogOpen(false)} 
          user={selectedUser} 
          statusData={statusData} 
          setStatusData={setStatusData} 
          onSubmit={handleStatusSubmit} 
        />
        
        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          show={showDeleteModal}
          title="Delete User"
          message={`Are you sure you want to delete ${userToDelete?.name || 'this user'}? This action cannot be undone.`}
          onCancel={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          confirmText="Delete User"
          cancelText="Cancel"
          loading={isDeleting}
        />

        {/* Pagination */}
        <div className="px-4 sm:px-6 py-3 flex flex-col md:flex-row items-center justify-between border-t border-gray-200 gap-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-2 text-sm text-gray-700">
            <div>
              <span className="font-medium">Page {currentPage}</span> of{' '}
              <span className="font-medium">{pagination?.pages || 1}</span>
              {pagination?.total && (
                <span className="ml-2 text-gray-500">({pagination.total} total users)</span>
              )}
            </div>
            {itemRange && <div className="text-xs text-gray-500">{itemRange}</div>}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <div className="flex items-center space-x-2">
              <label htmlFor="users-per-page" className="text-sm text-gray-600 whitespace-nowrap">
                Users per page:
              </label>
              <select
                id="users-per-page"
                value={pageSize}
                onChange={e => { setPageSize(Number(e.target.value)); setCurrentPage(1); }}
                className="px-2 py-1 pr-7 border rounded-md text-sm bg-white"
              >
                {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>

            <button
              onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>

            <div className="flex items-center space-x-1">
              {pagination?.pages && pagination.pages <= 5
                ? Array.from({ length: pagination.pages }, (_, i) => i + 1).map(p => (
                    <button
                      key={p}
                      onClick={() => setCurrentPage(p)}
                      className={`px-3 py-2 text-sm rounded-md ${
                        p === currentPage
                          ? 'bg-btn-primary text-white'
                          : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {p}
                    </button>
                  ))
                : <span className="px-3 py-2 text-sm bg-gray-100 rounded-md">{currentPage}</span>}
            </div>

            <button
              onClick={() => setCurrentPage(p => (p < (pagination?.pages || 1) ? p + 1 : p))}
              disabled={currentPage >= (pagination?.pages || 1)}
              className="px-3 py-2 text-sm bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagement;